import {
  getTeamsByTeamExternalIdTrendsOptions,
  TeamTrendsHitterStats,
  TeamTrendsPitcherPracticeStats,
  TeamTrendsPitcherStats,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { useMemo } from "react";

import { getCalendarYearRange } from "@/utils/dateUtils";

export type TrendsUnit = "MPH" | "FT" | "IN" | "%";

export type MetricImprovement = "lower" | "higher";

type TrendsMetricData = {
  name: string;
  metric:
    | keyof TeamTrendsHitterStats
    | keyof TeamTrendsPitcherStats
    | keyof TeamTrendsPitcherPracticeStats;
  unit: TrendsUnit;
  improvement: MetricImprovement;
};

const hittingMetrics: TrendsMetricData[] = [
  {
    name: "Exit Velocity",
    metric: "avg_exit_velocity",
    unit: "MPH",
    improvement: "higher",
  },
  {
    name: "Hard Hit %",
    metric: "hard_hit_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Avg Proj. Distance",
    metric: "avg_distance",
    unit: "FT",
    improvement: "higher",
  },
  {
    name: "Whiff %",
    metric: "whiff_rate",
    unit: "%",
    improvement: "lower",
  },
  {
    name: "Avg EV in Air",
    metric: "avg_ev_in_air",
    unit: "MPH",
    improvement: "higher",
  },
  {
    name: "Avg EV on GB",
    metric: "avg_ev_on_gb",
    unit: "MPH",
    improvement: "higher",
  },
  {
    name: "Contact %",
    metric: "contact_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Chase %",
    metric: "chase_rate",
    unit: "%",
    improvement: "lower",
  },
  {
    name: "Ground Ball %",
    metric: "ground_ball_rate",
    unit: "%",
    improvement: "lower",
  },
  {
    name: "SwStr %",
    metric: "swstr_rate",
    unit: "%",
    improvement: "lower",
  },
];

const pitchingMetrics: TrendsMetricData[] = [
  {
    name: "EV Against",
    metric: "avg_ev_against",
    unit: "MPH",
    improvement: "lower",
  },
  {
    name: "EV Against in Air",
    metric: "avg_ev_against_in_air",
    unit: "MPH",
    improvement: "lower",
  },
  {
    name: "EV Against on Ground",
    metric: "avg_ev_against_on_ground",
    unit: "MPH",
    improvement: "lower",
  },
  {
    name: "Whiff %",
    metric: "whiff_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "FPS % (First Pitch Strike)",
    metric: "fps_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Fly Ball %",
    metric: "fly_ball_rate",
    unit: "%",
    improvement: "lower",
  },
  {
    name: "Ground Ball %",
    metric: "ground_ball_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Chase %",
    metric: "chase_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Contact %",
    metric: "contact_rate",
    unit: "%",
    improvement: "lower",
  },
  {
    name: "SwStr %",
    metric: "swstr_rate",
    unit: "%",
    improvement: "higher",
  },
];

const pitchingMetricsPractice: TrendsMetricData[] = [
  {
    name: "Avg Fastball Velocity",
    metric: "avg_fb_velocity",
    unit: "MPH",
    improvement: "higher",
  },
  {
    name: "Avg Fastball Strike %",
    metric: "avg_fb_strike_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Avg Fastball Total Break",
    metric: "avg_fb_total_break",
    unit: "IN",
    improvement: "higher",
  },
  {
    name: "Avg Change Up Velocity",
    metric: "avg_change_up_velocity",
    unit: "MPH",
    improvement: "lower",
  },
  {
    name: "Avg FB vs CH Velocity Diff",
    metric: "avg_fb_vs_ch_velocity",
    unit: "MPH",
    improvement: "higher",
  },
  {
    name: "Avg Change Up Strike %",
    metric: "avg_change_up_strike_rate",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Avg Breaking Ball Velocity",
    metric: "avg_breaking_ball_velocity",
    unit: "%",
    improvement: "higher",
  },
  {
    name: "Avg Breaking Ball Total Break",
    metric: "avg_breaking_ball_total_break",
    unit: "IN",
    improvement: "higher",
  },
  {
    name: "Total Avg Strike %",
    metric: "total_avg_strike_rate",
    unit: "%",
    improvement: "higher",
  },
];

const SAMPLE_HITTING_SEASON: TeamTrendsHitterStats = {
  avg_exit_velocity: 87.5,
  hard_hit_rate: 0.38,
  avg_distance: 285,
  whiff_rate: 0.24,
  avg_ev_in_air: 91.2,
  avg_ev_on_gb: 82.3,
  contact_rate: 0.76,
  chase_rate: 0.28,
  ground_ball_rate: 0.42,
  swstr_rate: 0.11,
};

const SAMPLE_HITTING_LAST30: TeamTrendsHitterStats = {
  avg_exit_velocity: 89.2,
  hard_hit_rate: 0.41,
  avg_distance: 292,
  whiff_rate: 0.22,
  avg_ev_in_air: 93.5,
  avg_ev_on_gb: 83.8,
  contact_rate: 0.78,
  chase_rate: 0.25,
  ground_ball_rate: 0.39,
  swstr_rate: 0.1,
};

const SAMPLE_PITCHING_SEASON: TeamTrendsPitcherStats = {
  avg_ev_against: 86.3,
  avg_ev_against_in_air: 89.5,
  avg_ev_against_on_ground: 81.2,
  whiff_rate: 0.26,
  fps_rate: 0.62,
  fly_ball_rate: 0.35,
  ground_ball_rate: 0.45,
  chase_rate: 0.31,
  contact_rate: 0.74,
  swstr_rate: 0.12,
};

const SAMPLE_PITCHING_LAST30: TeamTrendsPitcherStats = {
  avg_ev_against: 84.8,
  avg_ev_against_in_air: 87.2,
  avg_ev_against_on_ground: 80.1,
  whiff_rate: 0.29,
  fps_rate: 0.65,
  fly_ball_rate: 0.32,
  ground_ball_rate: 0.48,
  chase_rate: 0.34,
  contact_rate: 0.71,
  swstr_rate: 0.14,
};

const SAMPLE_PITCHING_PRACTICE_SEASON: TeamTrendsPitcherPracticeStats = {
  avg_fb_velocity: 92.5,
  avg_fb_strike_rate: 0.64,
  avg_fb_total_break: 14.2,
  avg_change_up_velocity: 82.3,
  avg_fb_vs_ch_velocity: 10.2,
  avg_change_up_strike_rate: 0.58,
  avg_breaking_ball_velocity: 78.5,
  avg_breaking_ball_total_break: 12.8,
  total_avg_strike_rate: 0.61,
};

const SAMPLE_PITCHING_PRACTICE_LAST30: TeamTrendsPitcherPracticeStats = {
  avg_fb_velocity: 93.8,
  avg_fb_strike_rate: 0.67,
  avg_fb_total_break: 15.1,
  avg_change_up_velocity: 81.5,
  avg_fb_vs_ch_velocity: 12.3,
  avg_change_up_strike_rate: 0.61,
  avg_breaking_ball_velocity: 79.2,
  avg_breaking_ball_total_break: 13.5,
  total_avg_strike_rate: 0.64,
};

// TODO: Set to false to use real API data
const USE_SAMPLE_DATA = true;

export const useTeamTrends = (
  teamId: string,
  filter: "pitching" | "hitting",
  gameType: "game" | "bullpen" | "batting_practice" = "game",
  year: number = new Date().getFullYear(),
) => {
  const { startDate: startOfYearDate, endDate: endOfYearDate } =
    getCalendarYearRange(year);
  const currentDate = new Date();
  const last30DaysDate = subDays(currentDate, 30);

  const seasonData = useQuery({
    ...getTeamsByTeamExternalIdTrendsOptions({
      path: { team_external_id: teamId },
      query: {
        gameType,
        startDate: startOfYearDate,
        endDate: endOfYearDate,
        playerType: filter === "hitting" ? "hitter" : "pitcher",
      },
    }),
    enabled: !USE_SAMPLE_DATA && !!teamId,
  });

  const last30dData = useQuery({
    ...getTeamsByTeamExternalIdTrendsOptions({
      path: { team_external_id: teamId },
      query: {
        startDate: format(last30DaysDate, "yyyy-MM-dd"),
        endDate: format(currentDate, "yyyy-MM-dd"),
        gameType: gameType,
        playerType: filter === "hitting" ? "hitter" : "pitcher",
      },
    }),
    enabled: !USE_SAMPLE_DATA && !!teamId,
  });

  const teamHitterTrendsData = useMemo(() => {
    if (filter !== "hitting") return [];

    const hitterStatsSeason = USE_SAMPLE_DATA
      ? SAMPLE_HITTING_SEASON
      : ((seasonData.data as TeamTrendsHitterStats) ?? SAMPLE_HITTING_SEASON);
    const hitterStatsLast30Days = USE_SAMPLE_DATA
      ? SAMPLE_HITTING_LAST30
      : ((last30dData.data as TeamTrendsHitterStats) ?? SAMPLE_HITTING_LAST30);

    return hittingMetrics.map((hittingMetric) => {
      const hitterMetric = hittingMetric.metric as keyof TeamTrendsHitterStats;
      return getTrendsMetricData(
        hittingMetric,
        hitterStatsSeason[hitterMetric],
        hitterStatsLast30Days[hitterMetric],
      );
    });
  }, [seasonData, last30dData, filter]);

  const teamPitcherTrendsData = useMemo(() => {
    if (filter !== "pitching") return [];

    if (gameType === "bullpen") {
      const pitcherStatsSeason = USE_SAMPLE_DATA
        ? SAMPLE_PITCHING_PRACTICE_SEASON
        : ((seasonData.data as TeamTrendsPitcherPracticeStats) ??
          SAMPLE_PITCHING_PRACTICE_SEASON);
      const pitcherStatsLast30Days = USE_SAMPLE_DATA
        ? SAMPLE_PITCHING_PRACTICE_LAST30
        : ((last30dData.data as TeamTrendsPitcherPracticeStats) ??
          SAMPLE_PITCHING_PRACTICE_LAST30);
      return pitchingMetricsPractice.map((pitchingMetric) => {
        const pitcherMetric =
          pitchingMetric.metric as keyof TeamTrendsPitcherPracticeStats;
        return getTrendsMetricData(
          pitchingMetric,
          pitcherStatsSeason[pitcherMetric],
          pitcherStatsLast30Days[pitcherMetric],
        );
      });
    }

    const pitcherStatsSeason = USE_SAMPLE_DATA
      ? SAMPLE_PITCHING_SEASON
      : ((seasonData.data as TeamTrendsPitcherStats) ?? SAMPLE_PITCHING_SEASON);
    const pitcherStatsLast30Days = USE_SAMPLE_DATA
      ? SAMPLE_PITCHING_LAST30
      : ((last30dData.data as TeamTrendsPitcherStats) ??
        SAMPLE_PITCHING_LAST30);
    return pitchingMetrics.map((pitchingMetric) => {
      const pitcherMetric =
        pitchingMetric.metric as keyof TeamTrendsPitcherStats;
      return getTrendsMetricData(
        pitchingMetric,
        pitcherStatsSeason[pitcherMetric],
        pitcherStatsLast30Days[pitcherMetric],
      );
    });
  }, [filter, seasonData, last30dData, gameType]);

  return {
    hitting: teamHitterTrendsData,
    pitching: teamPitcherTrendsData,
    isLoadingTeamSeasonTrends: USE_SAMPLE_DATA ? false : seasonData.isLoading,
    isLoadingTeamLast30DaysDateTrends: USE_SAMPLE_DATA
      ? false
      : last30dData.isLoading,
    teamSeasonTrendsError: USE_SAMPLE_DATA ? null : seasonData.error,
    teamLast30DaysDateTrendsError: USE_SAMPLE_DATA ? null : last30dData.error,
  };
};

const getTrendsMetricData = (
  metric: TrendsMetricData,
  seasonData?: number | null,
  last30DaysData?: number | null,
) => {
  const isTrendDataValidForVsAvg =
    seasonData !== null &&
    seasonData !== undefined &&
    last30DaysData !== null &&
    last30DaysData !== undefined &&
    !isNaN(seasonData) &&
    !isNaN(last30DaysData);

  return {
    seasonAvg: seasonData,
    last30Days: last30DaysData,
    last30DaysVsAvg: isTrendDataValidForVsAvg
      ? last30DaysData - seasonData
      : undefined,
    metric: metric.name,
    improvement: metric.improvement,
    unit: metric.unit,
  };
};
